import { PaperAirplaneIcon, XMarkIcon, SparklesIcon, ArrowPathIcon } from "@heroicons/react/24/outline";
import React, { useState, useRef, useEffect, useCallback } from "react";
import { searchKnowledgeBase } from "../utils/supportSearch";
import { sendAIChatMessage } from "../utils/aiSupportApi";
import { useAIChat } from "../provider/AIChatProvider";

const SUGGESTED_QUESTIONS = [
  "How do I create a new event?",
  "How do I create a couple ticket?",
  "How to absorb the service fee?",
  "What payment methods can I accept?",
];

const INITIAL_WIDTH = 380;
const INITIAL_HEIGHT = 520;
const MIN_WIDTH = 300;
const MIN_HEIGHT = 350;
const MAX_WIDTH = 700;
const MAX_HEIGHT = 800;

function ChatMessage({ message, onSuggestionClick, onFollowUpClick }) {
  return (
    <div className={`flex ${message.role === "user" ? "justify-end" : "justify-start"} mb-3`}>
      <div className="max-w-[85%]">
        <div
          className={`px-4 py-2.5 rounded-2xl text-sm leading-relaxed whitespace-pre-wrap ${
            message.role === "user"
              ? "bg-primary text-white rounded-br-md"
              : "bg-gray-100 text-gray-800 rounded-bl-md"
          }`}
          dangerouslySetInnerHTML={{
            __html: message.role === "assistant"
              ? message.content
                  .replace(/\*\*(.*?)\*\*/g, "<strong>$1</strong>")
                  .replace(/\n/g, "<br/>")
              : message.content,
          }}
        />
        {/* Follow-up option buttons */}
        {message.followUpOptions && !message.followUpAnswered && (
          <div className="mt-2 flex flex-col gap-1.5">
            {message.followUpOptions.map((opt, idx) => (
              <button
                key={idx}
                onClick={() => onFollowUpClick(opt)}
                className="text-left text-sm px-3 py-2 rounded-xl border border-primary/30 text-primary font-medium hover:bg-primary/5 hover:border-primary/50 transition-colors"
              >
                {opt.label}
              </button>
            ))}
          </div>
        )}
        {/* Related suggestions */}
        {message.suggestions && message.suggestions.length > 0 && (
          <div className="mt-2 flex flex-col gap-1.5">
            <span className="text-xs text-gray-400 ml-1">Related questions:</span>
            {message.suggestions.map((s, idx) => (
              <button
                key={idx}
                onClick={() => onSuggestionClick(s)}
                className="text-left text-xs px-3 py-1.5 rounded-lg border border-gray-200 text-gray-500 hover:bg-gray-50 hover:border-gray-300 transition-colors"
              >
                {s}
              </button>
            ))}
          </div>
        )}
      </div>
    </div>
  );
}

function TypingIndicator() {
  return (
    <div className="flex justify-start mb-3">
      <div className="bg-gray-100 px-4 py-3 rounded-2xl rounded-bl-md flex gap-1.5 items-center">
        <span className="w-2 h-2 bg-gray-400 rounded-full animate-bounce" style={{ animationDelay: "0ms" }} />
        <span className="w-2 h-2 bg-gray-400 rounded-full animate-bounce" style={{ animationDelay: "150ms" }} />
        <span className="w-2 h-2 bg-gray-400 rounded-full animate-bounce" style={{ animationDelay: "300ms" }} />
      </div>
    </div>
  );
}

export default function AISupportChat() {
  const {
    messages,
    isOpen,
    position,
    closeChat,
    addMessage,
    updateMessages,
    setPosition,
    clearHistory,
  } = useAIChat();

  const [input, setInput] = useState("");
  const [isLoading, setIsLoading] = useState(false);
  const messagesEndRef = useRef(null);
  const inputRef = useRef(null);

  // Drag state (local only — no need to persist)
  const [isDragging, setIsDragging] = useState(false);
  const dragOffset = useRef({ x: 0, y: 0 });
  const windowRef = useRef(null);

  // Resize state
  const [size, setSize] = useState({ width: INITIAL_WIDTH, height: INITIAL_HEIGHT });
  const [isResizing, setIsResizing] = useState(false);
  const resizeStart = useRef({ x: 0, y: 0, width: 0, height: 0 });

  // Initialize position to bottom-right on first open
  useEffect(() => {
    if (isOpen && position.x === -1) {
      setPosition({
        x: window.innerWidth - size.width - 24,
        y: window.innerHeight - size.height - 24,
      });
    }
  }, [isOpen]);

  useEffect(() => {
      const handleWindowResize = () => {
          if (isOpen) {
          setPosition({
            x: Math.min(position.x, window.innerWidth - size.width - 8),
            y: Math.min(position.y, window.innerHeight - size.height - 8),
          });
        }
      };

      window.addEventListener("resize", handleWindowResize);
      return () => {
          window.removeEventListener("resize", handleWindowResize);
      };
  }, [isOpen, position, size]);

  useEffect(() => {
    messagesEndRef.current?.scrollIntoView({ behavior: "smooth" });
  }, [messages, isLoading]);

  useEffect(() => {
    if (isOpen && inputRef.current) {
      setTimeout(() => inputRef.current?.focus(), 100);
    }
  }, [isOpen]);

  // Mouse drag handlers
  const handleMouseDown = useCallback((e) => {
    if (e.target.closest('[data-no-drag]')) return;
    setIsDragging(true);
    const rect = windowRef.current.getBoundingClientRect();
    dragOffset.current = {
      x: e.clientX - rect.left,
      y: e.clientY - rect.top,
    };
    e.preventDefault();
  }, []);

  const handleMouseMove = useCallback((e) => {
    if (!isDragging) return;
    const newX = e.clientX - dragOffset.current.x;
    const newY = e.clientY - dragOffset.current.y;
    const maxX = window.innerWidth - size.width;
    const maxY = window.innerHeight - 60;
    setPosition({
      x: Math.max(0, Math.min(newX, maxX)),
      y: Math.max(0, Math.min(newY, maxY)),
    });
  }, [isDragging, size, setPosition]);

  const handleMouseUp = useCallback(() => {
    setIsDragging(false);
  }, []);

  useEffect(() => {
    if (isDragging) {
      document.addEventListener("mousemove", handleMouseMove);
      document.addEventListener("mouseup", handleMouseUp);
      return () => {
        document.removeEventListener("mousemove", handleMouseMove);
        document.removeEventListener("mouseup", handleMouseUp);
      };
    }
  }, [isDragging, handleMouseMove, handleMouseUp]);

  // Touch drag handlers
  const handleTouchStart = useCallback((e) => {
    if (e.target.closest('[data-no-drag]')) return;
    setIsDragging(true);
    const touch = e.touches[0];
    const rect = windowRef.current.getBoundingClientRect();
    dragOffset.current = {
      x: touch.clientX - rect.left,
      y: touch.clientY - rect.top,
    };
  }, []);

  const handleTouchMove = useCallback((e) => {
    if (!isDragging) return;
    const touch = e.touches[0];
    const newX = touch.clientX - dragOffset.current.x;
    const newY = touch.clientY - dragOffset.current.y;
    const maxX = window.innerWidth - size.width;
    const maxY = window.innerHeight - 60;
    setPosition({
      x: Math.max(0, Math.min(newX, maxX)),
      y: Math.max(0, Math.min(newY, maxY)),
    });
  }, [isDragging, size, setPosition]);

  const handleTouchEnd = useCallback(() => {
    setIsDragging(false);
  }, []);

  useEffect(() => {
    if (isDragging) {
      document.addEventListener("touchmove", handleTouchMove, { passive: false });
      document.addEventListener("touchend", handleTouchEnd);
      return () => {
        document.removeEventListener("touchmove", handleTouchMove);
        document.removeEventListener("touchend", handleTouchEnd);
      };
    }
  }, [isDragging, handleTouchMove, handleTouchEnd]);

  // Resize handlers
  const handleResizeStart = useCallback((e) => {
    e.preventDefault();
    e.stopPropagation();
    setIsResizing(true);
    const clientX = e.touches ? e.touches[0].clientX : e.clientX;
    const clientY = e.touches ? e.touches[0].clientY : e.clientY;
    resizeStart.current = { x: clientX, y: clientY, width: size.width, height: size.height };
  }, [size]);

  const handleResizeMove = useCallback((e) => {
    if (!isResizing) return;
    const clientX = e.touches ? e.touches[0].clientX : e.clientX;
    const clientY = e.touches ? e.touches[0].clientY : e.clientY;
    const newWidth = Math.max(MIN_WIDTH, Math.min(MAX_WIDTH, resizeStart.current.width + (clientX - resizeStart.current.x)));
    const newHeight = Math.max(MIN_HEIGHT, Math.min(MAX_HEIGHT, resizeStart.current.height + (clientY - resizeStart.current.y)));
    setSize({ width: newWidth, height: newHeight });
  }, [isResizing]);

  const handleResizeEnd = useCallback(() => {
    setIsResizing(false);
  }, []);

  useEffect(() => {
    if (isResizing) {
      document.addEventListener("mousemove", handleResizeMove);
      document.addEventListener("mouseup", handleResizeEnd);
      document.addEventListener("touchmove", handleResizeMove, { passive: false });
      document.addEventListener("touchend", handleResizeEnd);
      return () => {
        document.removeEventListener("mousemove", handleResizeMove);
        document.removeEventListener("mouseup", handleResizeEnd);
        document.removeEventListener("touchmove", handleResizeMove);
        document.removeEventListener("touchend", handleResizeEnd);
      };
    }
  }, [isResizing, handleResizeMove, handleResizeEnd]);

  const sendMessage = async (text) => {
    const userMessage = text || input.trim();
    if (!userMessage || isLoading) return;

    addMessage({ role: "user", content: userMessage });
    setInput("");
    setIsLoading(true);

    try {
      // Build conversation history for the LLM
      const chatHistory = [
        ...messages
          .filter((m) => m.role === "user" || m.role === "assistant")
          .map((m) => ({ role: m.role, content: m.content })),
        { role: "user", content: userMessage },
      ];

      const result = await sendAIChatMessage(chatHistory);
      addMessage({
        role: "assistant",
        content: result.message,
        suggestions: [],
      });
    } catch (error) {
      console.warn("LLM endpoint failed, falling back to local KB:", error);
      // Fallback to local keyword-based search
      const result = searchKnowledgeBase(userMessage);

      if (result.hasFollowUp) {
        addMessage({
          role: "assistant",
          content: result.followUp.question,
          followUpOptions: result.followUp.options,
          suggestions: result.suggestions || [],
        });
      } else {
        addMessage({
          role: "assistant",
          content: result.answer,
          suggestions: result.suggestions || [],
        });
      }
    }
    setIsLoading(false);
  };

  const handleFollowUpClick = async (option) => {
    // Mark follow-up messages as answered so buttons disappear, then add user choice + answer
    updateMessages((prev) => [
      ...prev.map((msg) =>
        msg.followUpOptions ? { ...msg, followUpAnswered: true } : msg
      ),
      { role: "user", content: option.label },
    ]);
    setIsLoading(true);
    await new Promise((resolve) => setTimeout(resolve, 300 + Math.random() * 300));
    addMessage({
      role: "assistant",
      content: option.answer,
      suggestions: [],
    });
    setIsLoading(false);
  };

  const handleKeyDown = (e) => {
    if (e.key === "Enter" && !e.shiftKey) {
      e.preventDefault();
      sendMessage();
    }
  };

  const showSuggestions = messages.length <= 1;

  if (!isOpen) return null;

  return (
    <div
      ref={windowRef}
      className="fixed z-50 bg-white rounded-2xl shadow-2xl flex flex-col border border-gray-200 overflow-hidden"
      style={{
        width: `${size.width}px`,
        height: `${size.height}px`,
        left: `${position.x}px`,
        top: `${position.y}px`,
        userSelect: isDragging || isResizing ? "none" : "auto",
      }}
    >
      {/* Header — draggable */}
      <div
        className="flex items-center justify-between px-4 py-3 bg-primary text-white cursor-grab active:cursor-grabbing rounded-t-2xl select-none"
        onMouseDown={handleMouseDown}
        onTouchStart={handleTouchStart}
      >
        <div className="flex items-center gap-2.5">
          <SparklesIcon className="w-5 h-5" />
          <div>
            <h3 className="font-semibold text-sm flex items-center gap-2">
              AI Assistant
              <span className="text-[9px] font-bold bg-white/20 text-white px-1.5 py-0.5 rounded uppercase tracking-wide">Beta</span>
            </h3>
            <p className="text-[11px] text-indigo-200">Ask me anything</p>
          </div>
        </div>
        <div className="flex items-center gap-1" data-no-drag>
          {messages.length > 1 && (
            <button
              onClick={clearHistory}
              className="p-1 rounded-lg hover:bg-white/10 transition-colors"
              title="New chat"
            >
              <ArrowPathIcon className="w-5 h-5" />
            </button>
          )}
          <button
            onClick={closeChat}
            className="p-1 rounded-lg hover:bg-white/10 transition-colors"
          >
            <XMarkIcon className="w-5 h-5" />
          </button>
        </div>
      </div>

      {/* Messages */}
      <div className="flex-1 overflow-y-auto px-4 py-4">
        {messages.map((msg, idx) => (
          <ChatMessage
            key={idx}
            message={msg}
            onSuggestionClick={(q) => sendMessage(q)}
            onFollowUpClick={handleFollowUpClick}
          />
        ))}

        {showSuggestions && (
          <div className="mt-2 flex flex-col gap-2">
            {SUGGESTED_QUESTIONS.map((q, idx) => (
              <button
                key={idx}
                onClick={() => sendMessage(q)}
                className="text-left text-sm px-3 py-2 rounded-xl border border-gray-200 text-gray-600 hover:bg-gray-50 hover:border-gray-300 transition-colors"
              >
                {q}
              </button>
            ))}
          </div>
        )}

        {isLoading && <TypingIndicator />}
        <div ref={messagesEndRef} />
      </div>

      {/* Input */}
      <div className="border-t border-gray-200 px-3 py-2.5">
        <div className="flex items-end gap-2">
          <textarea
            ref={inputRef}
            data-no-drag
            value={input}
            onChange={(e) => setInput(e.target.value)}
            onKeyDown={handleKeyDown}
            placeholder="Type your question..."
            rows={1}
            className="flex-1 resize-none rounded-xl border border-gray-300 px-3 py-2 text-sm focus:outline-none focus:border-primary focus:ring-1 focus:ring-primary max-h-28"
            style={{ minHeight: "38px" }}
          />
          <button
            data-no-drag
            onClick={() => sendMessage()}
            disabled={!input.trim() || isLoading}
            className="p-2 rounded-xl bg-primary text-white disabled:opacity-40 hover:bg-light-primary transition-colors shrink-0"
          >
            <PaperAirplaneIcon className="w-4 h-4" />
          </button>
        </div>
      </div>

      {/* Resize handle */}
      <div
        onMouseDown={handleResizeStart}
        onTouchStart={handleResizeStart}
        className="absolute bottom-0 right-0 w-4 h-4 cursor-se-resize"
        style={{
          background: "linear-gradient(135deg, transparent 50%, rgba(156,163,175,0.5) 50%)",
          borderRadius: "0 0 0.75rem 0",
        }}
      />
    </div>
  );
}
